import React, { useState, useCallback } from 'react';
import * as XLSX from 'xlsx';
import { apiClient } from '../../../api/client';
import {
  PRIMARY,
  TEXT_MUTED,
  BORDER_LIGHT,
  BG_CARD,
  BG_SURFACE,
  TEXT_DARK,
} from '../table-management/designTokens';

// ── Types ─────────────────────────────────────────────────────

interface CountAlignmentByEvent {
  eventId: string;
  eventTitle: string;
  registrations: number;
  tickets: number;
  guestRecords: number;
  unlinkedGuestRecords: number;
}

interface TicketCountMismatch {
  orderId: string;
  referenceId: string;
  purchaserName: string;
  expectedCount: number;
  actualCount: number;
  difference: number;
}

interface DuplicateNameInOrder {
  orderId: string;
  referenceId: string;
  purchaserName: string;
  duplicateName: string;
  occurrences: number;
}

interface ReconciliationReport {
  totals: {
    guestRecords: number;
    tickets: number;
    registrations: number;
    orders: number;
  };
  countAlignment: CountAlignmentByEvent[];
  ticketCountMismatches: TicketCountMismatch[];
  duplicateNamesInOrders: DuplicateNameInOrder[];
  orphanGuestRecords: {
    count: number;
    records: { id: string; fullName: string; ticketNumber: string | null; createdAt: string }[];
  };
  registrationsWithNoTicket: {
    count: number;
    records: { id: string; userName: string; userEmail: string | null; eventTitle: string; createdAt: string }[];
  };
}

interface FileOrder {
  referenceId: string;
  ticketCount: number;
}

interface FileCompareResult {
  inFileNotSystem: FileOrder[];
  inSystemNotFile: { referenceId: string; purchaserName: string; ticketCount: number }[];
  ticketCountMismatches: { referenceId: string; fileCount: number; systemCount: number; purchaserName: string }[];
  summary: { fileOrders: number; systemOrders: number; matched: number };
}

// ── Styles ────────────────────────────────────────────────────

const sectionStyle: React.CSSProperties = {
  backgroundColor: BG_CARD,
  border: `1px solid ${BORDER_LIGHT}`,
  borderRadius: '8px',
  padding: '1.25rem',
  marginBottom: '1rem',
};

const sectionTitleStyle: React.CSSProperties = {
  fontWeight: 700,
  fontSize: '0.95rem',
  color: TEXT_DARK,
  marginBottom: '0.75rem',
  cursor: 'pointer',
  userSelect: 'none',
};

const primaryBtnStyle: React.CSSProperties = {
  backgroundColor: PRIMARY,
  color: '#fff',
  border: 'none',
  borderRadius: '6px',
  padding: '8px 16px',
  fontSize: '0.85rem',
  fontWeight: 600,
  cursor: 'pointer',
};

const tableStyle: React.CSSProperties = {
  borderCollapse: 'collapse',
  width: '100%',
  fontSize: '0.85rem',
};

const thStyle: React.CSSProperties = {
  textAlign: 'left',
  padding: '8px 10px',
  borderBottom: `2px solid ${BORDER_LIGHT}`,
  color: TEXT_DARK,
  fontWeight: 600,
  fontSize: '0.8rem',
};

const tdStyle: React.CSSProperties = {
  padding: '6px 10px',
  borderBottom: `1px solid ${BORDER_LIGHT}`,
  color: TEXT_DARK,
};

const statCardStyle: React.CSSProperties = {
  backgroundColor: BG_SURFACE,
  border: `1px solid ${BORDER_LIGHT}`,
  borderRadius: '8px',
  padding: '0.75rem 1rem',
  textAlign: 'center',
};

const statValueStyle: React.CSSProperties = {
  fontSize: '1.5rem',
  fontWeight: 700,
  color: PRIMARY,
  margin: 0,
};

const statLabelStyle: React.CSSProperties = {
  fontSize: '0.75rem',
  color: TEXT_MUTED,
  margin: '4px 0 0',
};

const badgeStyle = (color: string, bg: string): React.CSSProperties => ({
  display: 'inline-block',
  padding: '2px 8px',
  borderRadius: '10px',
  fontSize: '0.75rem',
  fontWeight: 600,
  color,
  backgroundColor: bg,
});

const uploadAreaStyle: React.CSSProperties = {
  border: `2px dashed ${BORDER_LIGHT}`,
  borderRadius: '8px',
  padding: '2rem',
  textAlign: 'center',
  cursor: 'pointer',
  backgroundColor: BG_SURFACE,
  transition: 'border-color 0.2s',
};

// ── Component ─────────────────────────────────────────────────

export function ReconciliationTab() {
  // Section collapse state
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    audit: true,
    compare: true,
  });

  // System Audit state
  const [report, setReport] = useState<ReconciliationReport | null>(null);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditError, setAuditError] = useState<string | null>(null);

  // File Compare state
  const [fileResult, setFileResult] = useState<FileCompareResult | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [fileLoading, setFileLoading] = useState(false);

  const toggleSection = (key: string) => {
    setExpandedSections((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  // ── System Audit ──────────────────────────────────────────

  const handleRunAudit = async () => {
    setAuditLoading(true);
    setAuditError(null);
    try {
      const data = await apiClient<ReconciliationReport>('/order-data-management/reconciliation');
      setReport(data);
    } catch (err: any) {
      setAuditError(err?.message || 'Failed to run reconciliation audit');
    } finally {
      setAuditLoading(false);
    }
  };

  // ── File Compare ──────────────────────────────────────────

  const handleFileUpload = useCallback(async (file: File) => {
    setFileLoading(true);
    setFileError(null);
    setFileResult(null);
    setFileName(file.name);

    try {
      // Parse file client-side
      const buffer = await file.arrayBuffer();
      const workbook = XLSX.read(buffer, { type: 'array' });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const rows: any[][] = XLSX.utils.sheet_to_json(sheet, { header: 1 });

      // Find header row (look for "Reference #" in first 5 rows)
      let headerIdx = -1;
      let refCol = -1;
      let countCol = -1;

      for (let i = 0; i < Math.min(rows.length, 10); i++) {
        const row = rows[i];
        if (!row) continue;
        for (let c = 0; c < row.length; c++) {
          const cell = String(row[c] || '').trim().toLowerCase();
          if (cell === 'reference #' || cell === 'reference#' || cell === 'reference') {
            refCol = c;
            headerIdx = i;
          }
          if (cell.includes('how many tickets') || cell.includes('ticket') && cell.includes('purchas')) {
            countCol = c;
          }
        }
        if (headerIdx >= 0) break;
      }

      // Fallback: col B (index 1) for Reference #, col G (index 6) for ticket count
      if (refCol < 0) refCol = 1;
      if (countCol < 0) countCol = 6;
      if (headerIdx < 0) headerIdx = 0;

      // Parse data rows
      const fileOrders: FileOrder[] = [];
      for (let i = headerIdx + 1; i < rows.length; i++) {
        const row = rows[i];
        if (!row || !row[refCol]) continue;
        const referenceId = String(row[refCol]).trim();
        if (!referenceId) continue;
        const ticketCount = parseInt(String(row[countCol] || '0'), 10) || 0;
        fileOrders.push({ referenceId, ticketCount });
      }

      if (fileOrders.length === 0) {
        setFileError('No order data found in the file. Check that the file has a "Reference #" column.');
        setFileLoading(false);
        return;
      }

      // Fetch system orders for comparison
      const systemOrders = await apiClient<{
        referenceId: string;
        purchaserName: string;
        ticketCount: number;
      }[]>('/order-data-management/reconciliation/system-orders');

      // Build lookup maps
      const systemMap = new Map(systemOrders.map((o) => [o.referenceId, o]));
      const fileMap = new Map(fileOrders.map((o) => [o.referenceId, o]));

      // In file but not system
      const inFileNotSystem = fileOrders.filter((fo) => !systemMap.has(fo.referenceId));

      // In system but not file
      const inSystemNotFile = systemOrders.filter((so) => !fileMap.has(so.referenceId));

      // Ticket count mismatches
      const ticketMismatches: FileCompareResult['ticketCountMismatches'] = [];
      for (const fo of fileOrders) {
        const sys = systemMap.get(fo.referenceId);
        if (sys && fo.ticketCount !== sys.ticketCount) {
          ticketMismatches.push({
            referenceId: fo.referenceId,
            fileCount: fo.ticketCount,
            systemCount: sys.ticketCount,
            purchaserName: sys.purchaserName,
          });
        }
      }

      const matched = fileOrders.filter(
        (fo) => systemMap.has(fo.referenceId) && systemMap.get(fo.referenceId)!.ticketCount === fo.ticketCount,
      ).length;

      setFileResult({
        inFileNotSystem,
        inSystemNotFile,
        ticketCountMismatches: ticketMismatches,
        summary: { fileOrders: fileOrders.length, systemOrders: systemOrders.length, matched },
      });
    } catch (err: any) {
      setFileError(err?.message || 'Failed to parse file');
    } finally {
      setFileLoading(false);
    }
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      const file = e.dataTransfer.files[0];
      if (file) handleFileUpload(file);
    },
    [handleFileUpload],
  );

  const handleFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) handleFileUpload(file);
      e.target.value = '';
    },
    [handleFileUpload],
  );

  return (
    <div style={{ padding: '0.5rem 0' }}>
      {/* Section 1: System Audit */}
      <div style={sectionStyle}>
        <div
          style={sectionTitleStyle}
          onClick={() => toggleSection('audit')}
          aria-expanded={expandedSections.audit}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === 'Enter' && toggleSection('audit')}
        >
          {expandedSections.audit ? '▾' : '▸'} 📊 System Audit
        </div>
        {expandedSections.audit && (
          <div>
            <p style={{ fontSize: '0.85rem', color: TEXT_MUTED, margin: '0 0 0.75rem' }}>
              Run a comprehensive audit comparing GuestRecords, Tickets, Registrations, and Orders across the system.
            </p>
            <button
              style={primaryBtnStyle}
              onClick={handleRunAudit}
              disabled={auditLoading}
              aria-label="Run system audit"
            >
              {auditLoading ? 'Running Audit...' : '🔄 Run System Audit'}
            </button>

            {auditError && (
              <p style={{ color: '#d32f2f', fontSize: '0.85rem', marginTop: '0.5rem' }}>{auditError}</p>
            )}

            {report && !auditLoading && (
              <div style={{ marginTop: '1rem' }}>
                {/* Totals */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '0.75rem', marginBottom: '1.25rem' }}>
                  <div style={statCardStyle}>
                    <p style={statValueStyle}>{report.totals.guestRecords}</p>
                    <p style={statLabelStyle}>Guest Records</p>
                  </div>
                  <div style={statCardStyle}>
                    <p style={statValueStyle}>{report.totals.tickets}</p>
                    <p style={statLabelStyle}>Tickets</p>
                  </div>
                  <div style={statCardStyle}>
                    <p style={statValueStyle}>{report.totals.registrations}</p>
                    <p style={statLabelStyle}>Registrations</p>
                  </div>
                  <div style={statCardStyle}>
                    <p style={statValueStyle}>{report.totals.orders}</p>
                    <p style={statLabelStyle}>Orders</p>
                  </div>
                </div>

                {/* Count Alignment by Event */}
                {report.countAlignment.length > 0 && (
                  <div style={{ marginBottom: '1.25rem' }}>
                    <h4 style={{ fontSize: '0.85rem', fontWeight: 600, color: TEXT_DARK, marginBottom: '0.5rem' }}>
                      Count Alignment by Event
                    </h4>
                    <div style={{ overflowX: 'auto' }}>
                      <table style={tableStyle}>
                        <thead>
                          <tr>
                            <th style={thStyle}>Event</th>
                            <th style={thStyle}>Registrations</th>
                            <th style={thStyle}>Tickets</th>
                            <th style={thStyle}>Guest Records</th>
                            <th style={thStyle}>Unlinked Guests</th>
                          </tr>
                        </thead>
                        <tbody>
                          {report.countAlignment.map((row) => (
                            <tr key={row.eventId}>
                              <td style={{ ...tdStyle, fontWeight: 600 }}>{row.eventTitle}</td>
                              <td style={tdStyle}>{row.registrations}</td>
                              <td style={tdStyle}>{row.tickets}</td>
                              <td style={tdStyle}>{row.guestRecords}</td>
                              <td style={tdStyle}>
                                {row.unlinkedGuestRecords > 0 ? (
                                  <span style={badgeStyle('#e65100', '#fff3e0')}>{row.unlinkedGuestRecords}</span>
                                ) : (
                                  <span style={{ color: '#2e7d32' }}>0</span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Ticket Count Mismatches */}
                <div style={{ marginBottom: '1.25rem' }}>
                  <h4 style={{ fontSize: '0.85rem', fontWeight: 600, color: TEXT_DARK, marginBottom: '0.5rem' }}>
                    Ticket Count Mismatches (Order Expected vs Actual)
                    {report.ticketCountMismatches.length === 0 && (
                      <span style={{ ...badgeStyle('#2e7d32', '#e8f5e9'), marginLeft: '8px' }}>All OK</span>
                    )}
                  </h4>
                  {report.ticketCountMismatches.length > 0 && (
                    <div style={{ overflowX: 'auto' }}>
                      <table style={tableStyle}>
                        <thead>
                          <tr>
                            <th style={thStyle}>Order Ref</th>
                            <th style={thStyle}>Purchaser</th>
                            <th style={thStyle}>Expected</th>
                            <th style={thStyle}>Actual</th>
                            <th style={thStyle}>Difference</th>
                          </tr>
                        </thead>
                        <tbody>
                          {report.ticketCountMismatches.map((m) => (
                            <tr key={m.orderId}>
                              <td style={{ ...tdStyle, fontWeight: 600 }}>{m.referenceId}</td>
                              <td style={tdStyle}>{m.purchaserName}</td>
                              <td style={tdStyle}>{m.expectedCount}</td>
                              <td style={tdStyle}>{m.actualCount}</td>
                              <td style={tdStyle}>
                                <span style={badgeStyle(m.difference > 0 ? '#2e7d32' : '#c62828', m.difference > 0 ? '#e8f5e9' : '#ffebee')}>
                                  {m.difference > 0 ? '+' : ''}{m.difference}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>

                {/* Duplicate Names in Orders */}
                <div style={{ marginBottom: '1.25rem' }}>
                  <h4 style={{ fontSize: '0.85rem', fontWeight: 600, color: TEXT_DARK, marginBottom: '0.5rem' }}>
                    Duplicate/Placeholder Names Within Orders
                    {report.duplicateNamesInOrders.length === 0 && (
                      <span style={{ ...badgeStyle('#2e7d32', '#e8f5e9'), marginLeft: '8px' }}>None Found</span>
                    )}
                  </h4>
                  {report.duplicateNamesInOrders.length > 0 && (
                    <div style={{ overflowX: 'auto' }}>
                      <table style={tableStyle}>
                        <thead>
                          <tr>
                            <th style={thStyle}>Order Ref</th>
                            <th style={thStyle}>Purchaser</th>
                            <th style={thStyle}>Duplicate Name</th>
                            <th style={thStyle}>Occurrences</th>
                          </tr>
                        </thead>
                        <tbody>
                          {report.duplicateNamesInOrders.map((d, idx) => (
                            <tr key={`${d.orderId}-${idx}`}>
                              <td style={{ ...tdStyle, fontWeight: 600 }}>{d.referenceId}</td>
                              <td style={tdStyle}>{d.purchaserName}</td>
                              <td style={tdStyle}>{d.duplicateName}</td>
                              <td style={tdStyle}>
                                <span style={badgeStyle('#e65100', '#fff3e0')}>{d.occurrences}×</span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>

                {/* Orphan Guest Records */}
                <div style={{ marginBottom: '1.25rem' }}>
                  <h4 style={{ fontSize: '0.85rem', fontWeight: 600, color: TEXT_DARK, marginBottom: '0.5rem' }}>
                    Orphan Guest Records (No Registration)
                    <span style={{ ...badgeStyle(report.orphanGuestRecords.count > 0 ? '#e65100' : '#2e7d32', report.orphanGuestRecords.count > 0 ? '#fff3e0' : '#e8f5e9'), marginLeft: '8px' }}>
                      {report.orphanGuestRecords.count}
                    </span>
                  </h4>
                  {report.orphanGuestRecords.count > 0 && (
                    <div style={{ overflowX: 'auto' }}>
                      <table style={tableStyle}>
                        <thead>
                          <tr>
                            <th style={thStyle}>Name</th>
                            <th style={thStyle}>Ticket #</th>
                            <th style={thStyle}>Created</th>
                          </tr>
                        </thead>
                        <tbody>
                          {report.orphanGuestRecords.records.slice(0, 25).map((r) => (
                            <tr key={r.id}>
                              <td style={{ ...tdStyle, fontWeight: 600 }}>{r.fullName}</td>
                              <td style={tdStyle}>{r.ticketNumber || '—'}</td>
                              <td style={tdStyle}>{new Date(r.createdAt).toLocaleDateString()}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {report.orphanGuestRecords.count > 25 && (
                        <p style={{ fontSize: '0.8rem', color: TEXT_MUTED, marginTop: '0.5rem' }}>
                          Showing 25 of {report.orphanGuestRecords.count} orphan records
                        </p>
                      )}
                    </div>
                  )}
                </div>

                {/* Registrations with No Ticket */}
                <div>
                  <h4 style={{ fontSize: '0.85rem', fontWeight: 600, color: TEXT_DARK, marginBottom: '0.5rem' }}>
                    Registrations with No Linked Ticket
                    <span style={{ ...badgeStyle(report.registrationsWithNoTicket.count > 0 ? '#e65100' : '#2e7d32', report.registrationsWithNoTicket.count > 0 ? '#fff3e0' : '#e8f5e9'), marginLeft: '8px' }}>
                      {report.registrationsWithNoTicket.count}
                    </span>
                  </h4>
                  {report.registrationsWithNoTicket.count > 0 && (
                    <div style={{ overflowX: 'auto' }}>
                      <table style={tableStyle}>
                        <thead>
                          <tr>
                            <th style={thStyle}>User</th>
                            <th style={thStyle}>Email</th>
                            <th style={thStyle}>Event</th>
                            <th style={thStyle}>Created</th>
                          </tr>
                        </thead>
                        <tbody>
                          {report.registrationsWithNoTicket.records.slice(0, 25).map((r) => (
                            <tr key={r.id}>
                              <td style={{ ...tdStyle, fontWeight: 600 }}>{r.userName}</td>
                              <td style={tdStyle}>{r.userEmail || '—'}</td>
                              <td style={tdStyle}>{r.eventTitle}</td>
                              <td style={tdStyle}>{new Date(r.createdAt).toLocaleDateString()}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {report.registrationsWithNoTicket.count > 25 && (
                        <p style={{ fontSize: '0.8rem', color: TEXT_MUTED, marginTop: '0.5rem' }}>
                          Showing 25 of {report.registrationsWithNoTicket.count} records
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Section 2: File Compare */}
      <div style={sectionStyle}>
        <div
          style={sectionTitleStyle}
          onClick={() => toggleSection('compare')}
          aria-expanded={expandedSections.compare}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === 'Enter' && toggleSection('compare')}
        >
          {expandedSections.compare ? '▾' : '▸'} 📁 File Compare
        </div>
        {expandedSections.compare && (
          <div>
            <p style={{ fontSize: '0.85rem', color: TEXT_MUTED, margin: '0 0 0.75rem' }}>
              Upload a Formsite order export (.xlsx or .csv) to compare against system orders.
              The file is parsed in your browser — nothing is stored on the server.
            </p>

            {/* Upload area */}
            <div
              style={uploadAreaStyle}
              onDragOver={(e) => e.preventDefault()}
              onDrop={handleDrop}
            >
              <p style={{ margin: '0 0 8px', fontSize: '1.5rem' }}>📄</p>
              <p style={{ margin: '0 0 8px', fontSize: '0.9rem', fontWeight: 600, color: TEXT_DARK }}>
                Drop file here or click to browse
              </p>
              <p style={{ margin: '0 0 12px', fontSize: '0.8rem', color: TEXT_MUTED }}>
                Accepts .xlsx or .csv — Formsite order export format
              </p>
              <label style={{ ...primaryBtnStyle, display: 'inline-block' }}>
                Choose File
                <input
                  type="file"
                  accept=".xlsx,.csv,.xls"
                  onChange={handleFileInputChange}
                  style={{ display: 'none' }}
                  aria-label="Upload order file for comparison"
                />
              </label>
            </div>

            {fileLoading && (
              <p style={{ color: TEXT_MUTED, fontSize: '0.85rem', marginTop: '0.75rem' }}>
                Parsing and comparing...
              </p>
            )}

            {fileError && (
              <p style={{ color: '#d32f2f', fontSize: '0.85rem', marginTop: '0.75rem' }}>{fileError}</p>
            )}

            {fileResult && (
              <div style={{ marginTop: '1rem' }}>
                {/* Summary */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '0.75rem', marginBottom: '1.25rem' }}>
                  <div style={statCardStyle}>
                    <p style={statValueStyle}>{fileResult.summary.fileOrders}</p>
                    <p style={statLabelStyle}>Orders in File</p>
                  </div>
                  <div style={statCardStyle}>
                    <p style={statValueStyle}>{fileResult.summary.systemOrders}</p>
                    <p style={statLabelStyle}>Orders in System</p>
                  </div>
                  <div style={statCardStyle}>
                    <p style={{ ...statValueStyle, color: '#2e7d32' }}>{fileResult.summary.matched}</p>
                    <p style={statLabelStyle}>Matched</p>
                  </div>
                  <div style={statCardStyle}>
                    <p style={{ ...statValueStyle, color: fileResult.inFileNotSystem.length + fileResult.inSystemNotFile.length + fileResult.ticketCountMismatches.length > 0 ? '#d32f2f' : '#2e7d32' }}>
                      {fileResult.inFileNotSystem.length + fileResult.inSystemNotFile.length + fileResult.ticketCountMismatches.length}
                    </p>
                    <p style={statLabelStyle}>Issues Found</p>
                  </div>
                </div>

                {fileName && (
                  <p style={{ fontSize: '0.8rem', color: TEXT_MUTED, marginBottom: '1rem' }}>
                    Compared file: <strong>{fileName}</strong>
                  </p>
                )}

                {/* In File but Not System */}
                <div style={{ marginBottom: '1.25rem' }}>
                  <h4 style={{ fontSize: '0.85rem', fontWeight: 600, color: TEXT_DARK, marginBottom: '0.5rem' }}>
                    Orders in File but NOT in System
                    <span style={{ ...badgeStyle(fileResult.inFileNotSystem.length > 0 ? '#c62828' : '#2e7d32', fileResult.inFileNotSystem.length > 0 ? '#ffebee' : '#e8f5e9'), marginLeft: '8px' }}>
                      {fileResult.inFileNotSystem.length}
                    </span>
                  </h4>
                  {fileResult.inFileNotSystem.length > 0 && (
                    <div style={{ overflowX: 'auto' }}>
                      <table style={tableStyle}>
                        <thead>
                          <tr>
                            <th style={thStyle}>Reference ID</th>
                            <th style={thStyle}>Ticket Count (File)</th>
                          </tr>
                        </thead>
                        <tbody>
                          {fileResult.inFileNotSystem.map((o) => (
                            <tr key={o.referenceId}>
                              <td style={{ ...tdStyle, fontWeight: 600 }}>{o.referenceId}</td>
                              <td style={tdStyle}>{o.ticketCount}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>

                {/* In System but Not File */}
                <div style={{ marginBottom: '1.25rem' }}>
                  <h4 style={{ fontSize: '0.85rem', fontWeight: 600, color: TEXT_DARK, marginBottom: '0.5rem' }}>
                    Orders in System but NOT in File
                    <span style={{ ...badgeStyle(fileResult.inSystemNotFile.length > 0 ? '#e65100' : '#2e7d32', fileResult.inSystemNotFile.length > 0 ? '#fff3e0' : '#e8f5e9'), marginLeft: '8px' }}>
                      {fileResult.inSystemNotFile.length}
                    </span>
                  </h4>
                  {fileResult.inSystemNotFile.length > 0 && (
                    <div style={{ overflowX: 'auto' }}>
                      <table style={tableStyle}>
                        <thead>
                          <tr>
                            <th style={thStyle}>Reference ID</th>
                            <th style={thStyle}>Purchaser</th>
                            <th style={thStyle}>Tickets</th>
                          </tr>
                        </thead>
                        <tbody>
                          {fileResult.inSystemNotFile.map((o) => (
                            <tr key={o.referenceId}>
                              <td style={{ ...tdStyle, fontWeight: 600 }}>{o.referenceId}</td>
                              <td style={tdStyle}>{o.purchaserName}</td>
                              <td style={tdStyle}>{o.ticketCount}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>

                {/* Ticket Count Mismatches */}
                <div>
                  <h4 style={{ fontSize: '0.85rem', fontWeight: 600, color: TEXT_DARK, marginBottom: '0.5rem' }}>
                    Ticket Count Mismatches (File vs System)
                    <span style={{ ...badgeStyle(fileResult.ticketCountMismatches.length > 0 ? '#e65100' : '#2e7d32', fileResult.ticketCountMismatches.length > 0 ? '#fff3e0' : '#e8f5e9'), marginLeft: '8px' }}>
                      {fileResult.ticketCountMismatches.length}
                    </span>
                  </h4>
                  {fileResult.ticketCountMismatches.length > 0 && (
                    <div style={{ overflowX: 'auto' }}>
                      <table style={tableStyle}>
                        <thead>
                          <tr>
                            <th style={thStyle}>Reference ID</th>
                            <th style={thStyle}>Purchaser</th>
                            <th style={thStyle}>File Count</th>
                            <th style={thStyle}>System Count</th>
                            <th style={thStyle}>Difference</th>
                          </tr>
                        </thead>
                        <tbody>
                          {fileResult.ticketCountMismatches.map((m) => (
                            <tr key={m.referenceId}>
                              <td style={{ ...tdStyle, fontWeight: 600 }}>{m.referenceId}</td>
                              <td style={tdStyle}>{m.purchaserName}</td>
                              <td style={tdStyle}>{m.fileCount}</td>
                              <td style={tdStyle}>{m.systemCount}</td>
                              <td style={tdStyle}>
                                <span style={badgeStyle('#c62828', '#ffebee')}>
                                  {m.systemCount - m.fileCount > 0 ? '+' : ''}{m.systemCount - m.fileCount}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
